import { queryOptions, useQuery } from "@tanstack/react-query";
import type {
  CatalogUseCase,
  NormalizedSignUpConsentCheckboxesConfig,
  NormalizedSignUpGeoRestrictionConfig,
} from "@blueskydataplatform/shared-ui/types/platform.types";
import { normalizeSignUpConsentCheckboxes } from "@blueskydataplatform/shared-ui/utils/sign-up-consent-checkboxes";
import { normalizeSignUpGeoRestriction } from "@blueskydataplatform/shared-ui/utils/sign-up-geo-restriction";
import { publicApi } from "@blueskydataplatform/shared-ui/lib/api-client";

export interface PlatformUseCasesResult {
  useCases: CatalogUseCase[];
  consentCheckboxes: NormalizedSignUpConsentCheckboxesConfig;
  geoRestriction: NormalizedSignUpGeoRestrictionConfig;
}

const getPlatformUseCases = async (): Promise<PlatformUseCasesResult> => {
  const { data } = await publicApi.get<{
    useCases: CatalogUseCase[];
    consentCheckboxes?: NormalizedSignUpConsentCheckboxesConfig;
    geoRestriction?: NormalizedSignUpGeoRestrictionConfig;
  }>("/platform/use-cases");
  return {
    useCases: data.useCases,
    consentCheckboxes: normalizeSignUpConsentCheckboxes(data.consentCheckboxes),
    geoRestriction: normalizeSignUpGeoRestriction(data.geoRestriction),
  };
};

export const getUseCasesQueryOptions = () =>
  queryOptions({
    queryKey: ["platform", "catalog", "use-cases"],
    queryFn: getPlatformUseCases,
    staleTime: 5 * 60 * 1000,
  });

export const useUseCases = () =>
  useQuery({
    ...getUseCasesQueryOptions(),
  });

/** @deprecated Use `useUseCases` — kept for callers that only need the array. */
export const useUseCasesList = () => {
  const query = useUseCases();
  return {
    ...query,
    data: query.data?.useCases,
  };
};
